<template>
  <SelectRoot
    v-model:open="isOpen"
    :model-value="modelValue"
    :disabled="isDisabled"
    @update:model-value="updateValue"
  >
    <SelectTrigger
      :id="fieldContext?.inputId"
      ref="trigger"
      :name="name"
      :class="[
        `design-system-input relative flex w-full items-center overflow-hidden rounded-lg border text-left text-sm tabular-nums text-current transition`,
        `outline-2 -outline-offset-1 focus-visible:outline`,
        isDisabled && `opacity-50`,
        fieldContext?.validationStatus.value === 'error'
          ? `border-red-300 outline-red-500 dark:border-red-800`
          : `outline-blue-500`,
      ]"
      :aria-invalid="fieldContext?.validationStatus.value === 'error'"
      :aria-describedby="ariaDescribedBy"
      @focus="onFocus"
      @blur="onBlur"
      @change="onChange"
    >
      <div class="min-w-0 grow truncate px-2.5 py-1.5">
        <SelectValue placeholder="&ZeroWidthSpace;">
          <slot name="selected" :option="selectedOption">
            {{ selectedOption?.label }}
          </slot>
        </SelectValue>
      </div>

      <div class="grid size-8 shrink-0 place-items-center">
        <ChevronDownIcon class="size-4 text-muted-foreground" />
      </div>
    </SelectTrigger>

    <SelectPortal>
      <Transition
        enter-active-class="transition-opacity duration-100 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <SelectContent
          :collision-padding="10"
          class="z-popover flex flex-col rounded-md bg-gray-900 py-2 tabular-nums text-white shadow-lg dark:border dark:border-border dark:bg-card"
        >
          <SelectScrollUpButton class="-mt-2 grid h-7 place-items-center">
            <ChevronDownIcon
              class="size-4 rotate-180 text-gray-400 dark:text-muted-foreground"
            />
          </SelectScrollUpButton>

          <SelectViewport>
            <template
              v-for="(children, groupName, idx) in groupedOptions"
              :key="groupName"
            >
              <SelectSeparator
                v-if="idx !== 0"
                class="my-2 border-t border-gray-700 dark:border-border"
              />

              <SelectLabel
                v-if="groupName"
                class="mx-5 my-1 shrink-0 select-none border-t-border text-[0.8125rem] leading-5 text-gray-400 dark:text-muted-foreground"
              >
                {{ groupName }}
              </SelectLabel>

              <SelectGroup>
                <SelectItem
                  v-for="it in children"
                  :key="it.id"
                  :value="it.id === '' ? INTERNAL_EMPTY_ID : it.id"
                  :disabled="it.disabled"
                  class="group relative mx-2 flex shrink-0 cursor-pointer select-none scroll-m-2 gap-3 self-stretch rounded-md border-none px-3 py-1 text-left text-sm outline-none transition-all duration-75 data-[highlighted]:bg-zinc-700 dark:data-[highlighted]:bg-muted"
                >
                  <div
                    class="flex min-w-0 grow flex-col break-words transition-opacity group-data-[disabled]:opacity-50"
                  >
                    <SelectItemText>
                      <slot name="option" :option="it">
                        {{ it.label }}
                      </slot>
                    </SelectItemText>

                    <p
                      v-if="it.description"
                      class="text-[0.8125rem] leading-5 text-muted-foreground"
                    >
                      {{ it.description }}
                    </p>
                  </div>

                  <CheckIcon
                    v-if="it.id !== '' || !it.disabled"
                    class="my-0.5 size-4 shrink-0 text-blue-400 opacity-0 transition-opacity duration-75 group-data-[state=checked]:opacity-100"
                  />
                </SelectItem>
              </SelectGroup>
            </template>
          </SelectViewport>

          <SelectScrollDownButton class="-mb-2 grid h-7 place-items-center">
            <ChevronDownIcon
              class="size-4 text-gray-400 dark:text-muted-foreground"
            />
          </SelectScrollDownButton>
        </SelectContent>
      </Transition>
    </SelectPortal>
  </SelectRoot>
</template>

<script setup lang="ts" generic="T extends string = string">
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectItemText,
  SelectLabel,
  SelectPortal,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from "reka-ui";
import { computed, onMounted, useId, useTemplateRef } from "vue";

import CheckIcon from "@/icons/heroicons/check-micro.svg";
import ChevronDownIcon from "@/icons/heroicons/chevron-down-micro.svg";
import { useKeybinds } from "@/lib/hotkeys";

import { injectFieldContext } from "./context";

export interface SelectOption<T extends string = string> {
  id: T;
  label: string;
  description?: string;
  disabled?: boolean;
  group?: string;
}

// Radix Vue doesn't support passing an empty string value, the expectation is
// that it would be used to show the placeholder set by `<SelectValue>`.
// We'll work around this by substituting the empty string
const INTERNAL_EMPTY_ID = `__empty_${useId()}`;

const modelValue = defineModel<T>({
  default: "" as T,
  get(v): T {
    return v === "" ? (INTERNAL_EMPTY_ID as T) : v;
  },
  set(v) {
    return v === INTERNAL_EMPTY_ID ? "" : v;
  },
});

const isOpen = defineModel<boolean>("open", { default: false });

const updateValue = (next: string) => {
  modelValue.value = next as T;
};

const props = defineProps<{
  options: readonly SelectOption<T>[];
  disabled?: boolean;
  name?: string;
  autofocus?: boolean;
  onFocus?: (event: FocusEvent) => void;
  onChange?: (event: Event) => void;
  onBlur?: (event: FocusEvent) => void;
}>();

const trigger = useTemplateRef("trigger");

const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});

const selectedOption = computed(() => {
  const currentValue =
    modelValue.value === INTERNAL_EMPTY_ID ? "" : modelValue.value;
  return props.options.find((opt) => opt.id === currentValue);
});

const groupedOptions = computed(() => {
  const ungroupedOptions: SelectOption[] = [];
  const groups: Record<string, SelectOption[]> = {};

  for (const option of props.options) {
    const groupName = option.group ?? "";

    if (groupName === "") {
      ungroupedOptions.push(option);
      continue;
    }

    const group = (groups[groupName] ??= []);
    group.push(option);
  }

  // Always sort ungrouped options first
  return ungroupedOptions.length ? { "": ungroupedOptions, ...groups } : groups;
});

useKeybinds(() => {
  if (!isOpen.value) {
    return;
  }

  return {
    Escape: {
      name: `Close select`,
      group: `Select`,
      run: (ev) => {
        if (ev.defaultPrevented) {
          return;
        }

        isOpen.value = false;
        return true;
      },
    },
  };
});

onMounted(() => {
  if (props.autofocus) {
    trigger.value?.$el?.focus({ preventScroll: true });
  }
});

defineExpose({
  input: computed(() => {
    return trigger.value?.$el as HTMLButtonElement | null | undefined;
  }),
});
</script>
